import React, { useState, useCallback, useEffect } from 'react'
import { token } from '@atlaskit/tokens'
import Avatar from '@atlaskit/avatar'
import Button from '@atlaskit/button'
import TextArea from '@atlaskit/textarea'
import { MAX_COMMENT_LENGTH } from '../../utils/formatters'
import { getUserInfo, isJiraConnect } from '../../utils/jiraContext'

/**
 * ADD COMMENT COMPONENT
 *
 * Input form for adding a new comment.
 * Shows current user's avatar, textarea, and submit button.
 *
 * Features:
 * - Current user avatar (from Jira context)
 * - Textarea with character counter
 * - Cmd/Ctrl+Enter keyboard shortcut to submit
 * - Escape to cancel/clear
 * - Loading state during submission
 *
 * @param {function} onSubmit - Callback when comment is submitted (receives content)
 * @param {function} onCancel - Optional callback to close/hide the form
 * @param {string} placeholder - Placeholder text for textarea
 * @param {boolean} autoFocus - Whether to auto-focus the textarea
 */
export function AddComment({
  onSubmit,
  onCancel,
  placeholder = 'Add a comment...',
  autoFocus = false
}) {
  // State for comment content
  const [content, setContent] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isFocused, setIsFocused] = useState(autoFocus)

  // State for current user info
  const [currentUser, setCurrentUser] = useState(null)

  /**
   * FETCH CURRENT USER
   * Gets current user info from Jira context on mount.
   */
  useEffect(() => {
    if (isJiraConnect()) {
      getUserInfo().then(user => {
        if (user) {
          setCurrentUser(user)
        }
      })
    }
  }, [])

  /**
   * HANDLE SUBMIT
   * Submits the comment and clears the form.
   */
  const handleSubmit = useCallback(async () => {
    const trimmedContent = content.trim()

    // Don't submit empty comments
    if (!trimmedContent) return

    setIsSubmitting(true)
    try {
      await onSubmit(trimmedContent)
      // Clear form on success
      setContent('')
      setIsFocused(false)
    } catch (error) {
      console.error('Failed to submit comment:', error)
      // Keep content on error so user can retry
    } finally {
      setIsSubmitting(false)
    }
  }, [content, onSubmit])

  /**
   * HANDLE CANCEL
   * Clears the form and optionally calls onCancel.
   */
  const handleCancel = useCallback(() => {
    setContent('')
    setIsFocused(false)
    if (onCancel) {
      onCancel()
    }
  }, [onCancel])

  /**
   * HANDLE KEYBOARD SHORTCUT
   * Cmd/Ctrl+Enter to submit, Escape to cancel.
   */
  const handleKeyDown = useCallback((e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      handleSubmit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancel()
    }
  }, [handleSubmit, handleCancel])

  /**
   * HANDLE FOCUS
   * Expand the form when focused.
   */
  const handleFocus = useCallback(() => {
    setIsFocused(true)
  }, [])

  // Show compact view when not focused and no content
  const isExpanded = isFocused || content.length > 0

  return (
    <div className="add-comment" style={{ marginBottom: '12px' }}>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
        {/* Current user avatar */}
        <Avatar
          size="small"
          name={currentUser?.userName || 'You'}
        />

        {/* Input area */}
        <div style={{ flex: 1 }}>
          <TextArea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            minimumRows={isExpanded ? 2 : 1}
            maxLength={MAX_COMMENT_LENGTH}
            placeholder={placeholder}
            isDisabled={isSubmitting}
            autoFocus={autoFocus}
          />

          {/* Actions row - only show when expanded */}
          {isExpanded && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '8px'
              }}
            >
              {/* Character counter */}
              <span
                style={{
                  fontSize: '11px',
                  color: content.length > MAX_COMMENT_LENGTH - 100
                    ? token('color.text.warning', '#FF8B00')
                    : token('color.text.subtlest', '#7A869A')
                }}
              >
                {MAX_COMMENT_LENGTH - content.length} characters remaining
              </span>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '8px' }}>
                {content.length > 0 && (
                  <Button
                    appearance="subtle"
                    spacing="compact"
                    onClick={handleCancel}
                    isDisabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  appearance="primary"
                  spacing="compact"
                  onClick={handleSubmit}
                  isLoading={isSubmitting}
                  isDisabled={!content.trim() || isSubmitting}
                >
                  Save
                </Button>
              </div>
            </div>
          )}

          {/* Keyboard hint - only show when expanded and not submitting */}
          {isExpanded && !isSubmitting && content.length > 0 && (
            <div
              style={{
                fontSize: '11px',
                color: token('color.text.subtlest', '#7A869A'),
                marginTop: '4px'
              }}
            >
              Press {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+Enter to save
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AddComment
